import React, { useState, useEffect, useRef, useMemo } from "react";
import { Send, Loader2, Bot, Info } from "lucide-react";
import "./AssistantChat.css";

export const AssistantChat = ({
  data,
  updateData,
  onNext,
  step = 3,
  totalSteps = 4,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  const scrollRef = useRef(null);
  const hasInitialized = useRef(false);

  // NOTE: In production, this should come from props or env vars, not hardcoded
  const token = process.env.REACT_APP_SIGNUP_AGENT_API_KEY || '#######################';

  const agent_id = process.env.REACT_APP_SIGNUP_AGENT_AGENT_ID || '1';

  const progressPct = useMemo(() => {
    const pct = Math.round((Number(step) / Number(totalSteps)) * 100);
    return Math.max(0, Math.min(100, pct));
  }, [step, totalSteps]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Initial Greeting
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      setIsLoading(true);
      setTimeout(() => {
        setMessages([
          {
            role: "model",
            text:
              "Welcome! I'm your Nexopta Onboarding Specialist. To start, could you tell me about your business type or industry?",
          },
        ]);
        setIsLoading(false);
      }, 600);
    }
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    const userEmail = data?.email || "No email provided";
    
    const userText = input;
    const apiMessage = `User Email: ${userEmail}\n\n${userText}`;
    setInput("");

    setMessages((prev) => [...prev, { role: "user", text: userText }]);
    setIsLoading(true);

    try {
      const apiResult = await sendMessageToAI(token,agent_id, apiMessage, conversationId);

      if (apiResult.conversation_id) setConversationId(apiResult.conversation_id);

      const responseText = apiResult.text || "No response text received.";
      setMessages((prev) => [...prev, { role: "model", text: responseText }]);

      updateData({
        useCaseDescription:
          (data?.useCaseDescription || "") +
          `\nUser: ${userText}\nAssistant: ${responseText}`,
      });
    } catch (err) {
      console.error("Chat Error:", err);
      setMessages((prev) => [
        ...prev,
        { role: "model", text: "I'm having trouble connecting. Let's continue for now." },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="nx-onboard-page">
      {/* Top bar */}
      <div className="nx-topbar">
        <div className="nx-brand">
          {/* Replace this with <img src="/your-logo.svg" .../> if you want */}
          <div className="nx-brand-mark">
          <img
            className="nx-brand-logo"
            src="/images/NexOpta_Icon.png"
            alt="Nexopta"
          /></div>
          <div className="nx-brand-word">NEXOPTA</div>
        </div>

        <div className="nx-step">
          Step {step} of {totalSteps}
        </div>
      </div>

      {/* Progress bar */}
      <div className="nx-progress-wrap">
        <div className="nx-progress-track">
          <div className="nx-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Content */}
      <div className="nx-content">
        <div className="nx-grid">
          {/* LEFT: Chat Card */}
          <div className="nx-chat-card">
            {/* Header */}
            <div className="nx-chat-header">
              <div className="nx-chat-identity">
                <div className="nx-avatar-lg">
                  <Bot size={20} />
                </div>
                <div className="nx-header-text">
                  <h3>Nexopta Agent</h3>
                  <p className="nx-status">
                    <span className="nx-dot" /> Online
                  </p>
                </div>
              </div>

              <button onClick={onNext} className="nx-skip">
                Skip / Continue &rarr;
              </button>
            </div>

            {/* Messages */}
            <div className="nx-messages" ref={scrollRef}>
              {messages.map((msg, idx) => {
                const isUser = msg.role === "user";
                return (
                  <div
                    key={idx}
                    className={`nx-row ${isUser ? "nx-row-user" : "nx-row-model"}`}
                  >
                    {!isUser && (
                      <div className="nx-avatar-sm">
                        <Bot size={16} />
                      </div>
                    )}

                    <div className={`nx-bubble ${isUser ? "nx-bubble-user" : "nx-bubble-model"}`}>
                      {msg.text}
                    </div>
                  </div>
                );
              })}

              {isLoading && (
                <div className="nx-row nx-row-model">
                  <div className="nx-avatar-sm">
                    <Bot size={16} />
                  </div>
                  <div className="nx-bubble nx-bubble-model nx-loading">
                    <Loader2 size={16} className="nx-spinner" />
                    <span>Agent is typing...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="nx-input">
              <div className="nx-input-row">
                <input
                  className="nx-input-box"
                  placeholder="Describe your business needs..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className="nx-send"
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  aria-label="Send message"
                >
                  <Send size={18} />
                </button>
              </div>

              <p className="nx-hint">
                Once you're satisfied, click 'Continue' at the top right.
              </p>
            </div>
          </div>

          {/* RIGHT: Info Panel */}
          <div className="nx-info-card">
            <div className="nx-info-header">
              <div className="nx-info-badge">
                <Info size={16} />
              </div>
              <h3>Why we need this</h3>
            </div>

            <p>
              We use this conversation to learn how your business runs and what systems you use.
              <br /><br />
              Our internal AI tools then use this information to set up the correct integrations and
              train your Business Assistant to work the way your business actually works, so it can
              start saving you time as quickly as possible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- API Service ---
async function sendMessageToAI(token,agent_id, message, conversationId) {
  const payload = {
    message,
    stream: false,
    ...(conversationId ? { conversation_id: conversationId } : {}),
  };

  const AGENT_ID =agent_id;
  const API_URL = `https://salesoptai-app-prod.icystone-9246cdc7.canadaeast.azurecontainerapps.io/api/ws/${AGENT_ID}/chat`;

  const response = await fetch(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": token,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let errorDetail = response.statusText;
    try {
      const errData = await response.json();
      if (errData.detail) errorDetail = errData.detail;
    } catch {}
    throw new Error(`API Error ${response.status}: ${errorDetail}`);
  }

  const data = await response.json();
  return {
    text: data.response || "",
    conversation_id: data.conversation_id,
  };
}

export default AssistantChat;
